// 🔗 Links Inventory - Complete URL Catalog

import { useState } from 'react';

interface Link {
  url: string;
  title: string;
  description: string;
  category: 'api' | 'docs' | 'external' | 'cloudflare' | 'project';
  status: 'active' | 'example' | 'template';
}

export function LinksInventory() {
  const [filter, setFilter] = useState<'all' | 'api' | 'docs' | 'external' | 'cloudflare' | 'project'>('all');
  const [searchTerm, setSearchTerm] = useState('');

  const apiUrl = import.meta.env.VITE_API_URL || 'https://your-api.workers.dev';
  const projectName = import.meta.env.VITE_PROJECT_NAME || 'FireStarter';

  const links: Link[] = [
    // === API Endpoints ===
    {
      url: `${apiUrl}/health`,
      title: 'Health Check',
      description: 'API health status and environment info',
      category: 'api',
      status: 'active',
    },
    {
      url: `${apiUrl}/bindings`,
      title: 'Bindings Status',
      description: 'CloudFlare bindings availability (D1, KV, R2, AI)',
      category: 'api',
      status: 'active',
    },
    {
      url: `${apiUrl}/api/validate/db`,
      title: 'D1 Database Test',
      description: 'Test D1 database CRUD operations (POST)',
      category: 'api',
      status: 'active',
    },
    {
      url: `${apiUrl}/api/validate/cache`,
      title: 'KV Cache Test',
      description: 'Test KV cache read/write operations (POST)',
      category: 'api',
      status: 'active',
    },
    {
      url: `${apiUrl}/api/upload`,
      title: 'R2 File Upload',
      description: 'Upload files to R2 storage (POST with multipart/form-data)',
      category: 'api',
      status: 'active',
    },
    {
      url: `${apiUrl}/api/download/:key`,
      title: 'R2 File Download',
      description: 'Download files from R2 storage (GET)',
      category: 'api',
      status: 'active',
    },
    {
      url: `${apiUrl}/api/ai/text`,
      title: 'AI Text Generation',
      description: 'Generate text using CloudFlare AI (POST)',
      category: 'api',
      status: 'active',
    },
    {
      url: `${apiUrl}/api/ai/embedding`,
      title: 'AI Embeddings',
      description: 'Generate embeddings using CloudFlare AI (POST)',
      category: 'api',
      status: 'active',
    },
    {
      url: `${apiUrl}/api/setup`,
      title: 'Setup Wizard',
      description: 'Create CloudFlare resources (D1, KV, R2) via API (POST)',
      category: 'api',
      status: 'active',
    },

    // === CloudFlare Dashboard ===
    {
      url: 'https://dash.cloudflare.com/',
      title: 'CloudFlare Dashboard',
      description: 'Main CloudFlare dashboard - manage all resources',
      category: 'cloudflare',
      status: 'active',
    },
    {
      url: 'https://dash.cloudflare.com/profile/api-tokens',
      title: 'API Tokens',
      description: 'Create and manage CloudFlare API tokens',
      category: 'cloudflare',
      status: 'active',
    },
    {
      url: 'https://developers.cloudflare.com/workers/',
      title: 'Workers Documentation',
      description: 'CloudFlare Workers docs and guides',
      category: 'docs',
      status: 'active',
    },
    {
      url: 'https://developers.cloudflare.com/d1/',
      title: 'D1 Database Docs',
      description: 'SQL database documentation and examples',
      category: 'docs',
      status: 'active',
    },
    {
      url: 'https://developers.cloudflare.com/kv/',
      title: 'KV Cache Docs',
      description: 'Key-value storage documentation',
      category: 'docs',
      status: 'active',
    },
    {
      url: 'https://developers.cloudflare.com/r2/',
      title: 'R2 Storage Docs',
      description: 'Object storage documentation (S3-compatible)',
      category: 'docs',
      status: 'active',
    },
    {
      url: 'https://developers.cloudflare.com/workers-ai/',
      title: 'Workers AI Docs',
      description: 'AI inference documentation and model catalog',
      category: 'docs',
      status: 'active',
    },

    // === Project Resources ===
    {
      url: 'https://github.com/cloudflare/workers-sdk',
      title: 'Wrangler CLI',
      description: 'Official CloudFlare Workers CLI tool',
      category: 'external',
      status: 'active',
    },
    {
      url: 'https://hono.dev/',
      title: 'Hono Framework',
      description: 'Fast web framework for CloudFlare Workers',
      category: 'external',
      status: 'active',
    },
    {
      url: 'https://orm.drizzle.team/',
      title: 'Drizzle ORM',
      description: 'TypeScript ORM for D1 database',
      category: 'external',
      status: 'active',
    },
    {
      url: 'https://react.dev/',
      title: 'React Documentation',
      description: 'Official React documentation',
      category: 'external',
      status: 'active',
    },
    {
      url: 'https://vitejs.dev/',
      title: 'Vite Documentation',
      description: 'Frontend build tool documentation',
      category: 'external',
      status: 'active',
    },
    {
      url: 'https://tailwindcss.com/',
      title: 'Tailwind CSS',
      description: 'Utility-first CSS framework',
      category: 'external',
      status: 'active',
    },

    // === Frontend Routes ===
    {
      url: '#dashboard',
      title: 'Dashboard',
      description: 'Main application dashboard',
      category: 'project',
      status: 'active',
    },
    {
      url: '#dev',
      title: 'Developer Hub',
      description: 'Self-documenting development tools and utilities',
      category: 'project',
      status: 'active',
    },
    {
      url: '#test',
      title: 'System Test',
      description: 'Verify all CloudFlare integrations',
      category: 'project',
      status: 'active',
    },
    {
      url: '#files',
      title: 'File Map',
      description: 'Project structure visualization',
      category: 'project',
      status: 'active',
    },
    {
      url: '#links',
      title: 'Links Inventory',
      description: 'Complete URL catalog (this page)',
      category: 'project',
      status: 'active',
    },
    {
      url: '#security',
      title: 'Security Audit',
      description: 'Vulnerability scanner and security checks',
      category: 'project',
      status: 'active',
    },
  ];

  const filteredLinks = links.filter(link => {
    const matchesFilter = filter === 'all' || link.category === filter;
    const matchesSearch = searchTerm === '' ||
      link.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      link.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      link.url.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const categoryCounts = links.reduce((acc, link) => {
    acc[link.category] = (acc[link.category] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-4xl">🔗</span>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Links Inventory</h1>
                <p className="text-sm text-gray-600">
                  {projectName} URL catalog - {links.length} total links
                </p>
              </div>
            </div>
            <a
              href="#dashboard"
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              ← Back to Dashboard
            </a>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search links..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Category Filter */}
            <div className="flex gap-2 flex-wrap">
              {(['all', 'api', 'docs', 'cloudflare', 'external', 'project'] as const).map((cat) => (
                <button
                  key={cat}
                  onClick={() => setFilter(cat)}
                  className={`px-4 py-2 rounded-lg font-medium transition ${
                    filter === cat
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {cat === 'all' ? 'All' : cat.charAt(0).toUpperCase() + cat.slice(1)}{' '}
                  {cat !== 'all' && `(${categoryCounts[cat] || 0})`}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="text-3xl font-bold text-blue-600">{categoryCounts.api || 0}</div>
            <div className="text-sm text-gray-600">API Endpoints</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="text-3xl font-bold text-green-600">{categoryCounts.docs || 0}</div>
            <div className="text-sm text-gray-600">Documentation</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="text-3xl font-bold text-orange-600">{categoryCounts.cloudflare || 0}</div>
            <div className="text-sm text-gray-600">CloudFlare</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="text-3xl font-bold text-purple-600">{categoryCounts.external || 0}</div>
            <div className="text-sm text-gray-600">External</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="text-3xl font-bold text-gray-600">{categoryCounts.project || 0}</div>
            <div className="text-sm text-gray-600">Project Routes</div>
          </div>
        </div>

        {/* Links List */}
        <div className="space-y-4">
          {filteredLinks.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
              <div className="text-5xl mb-4">🔍</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No links found</h3>
              <p className="text-gray-600">Try adjusting your search or filter</p>
            </div>
          ) : (
            filteredLinks.map((link, index) => (
              <div
                key={index}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-xl">
                        {link.category === 'api' ? '🔌' :
                         link.category === 'docs' ? '📚' :
                         link.category === 'cloudflare' ? '☁️' :
                         link.category === 'external' ? '🌐' :
                         '📱'}
                      </span>
                      <h3 className="text-lg font-semibold text-gray-900">{link.title}</h3>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        link.category === 'api' ? 'bg-blue-100 text-blue-700' :
                        link.category === 'docs' ? 'bg-green-100 text-green-700' :
                        link.category === 'cloudflare' ? 'bg-orange-100 text-orange-700' :
                        link.category === 'external' ? 'bg-purple-100 text-purple-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {link.category}
                      </span>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        link.status === 'active' ? 'bg-green-100 text-green-700' :
                        link.status === 'example' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {link.status}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-3">{link.description}</p>
                    <a
                      href={link.url}
                      target={link.url.startsWith('http') ? '_blank' : undefined}
                      rel={link.url.startsWith('http') ? 'noopener noreferrer' : undefined}
                      className="text-sm text-blue-600 hover:text-blue-700 font-mono break-all"
                    >
                      {link.url}
                    </a>
                  </div>
                  <a
                    href={link.url}
                    target={link.url.startsWith('http') ? '_blank' : undefined}
                    rel={link.url.startsWith('http') ? 'noopener noreferrer' : undefined}
                    className="ml-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-sm whitespace-nowrap"
                  >
                    Visit →
                  </a>
                </div>
              </div>
            ))
          )}
        </div>
      </main>
    </div>
  );
}
